import { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth, useLang } from "@/context/AppContext";

const RTCtx = createContext(null);

export const fmt = (t, n) => {
  const d = { ...n.data, state: n.data.state ? t(`st_${n.data.state}`) : "" };
  return t(`n_${n.type}`).replace(/\{(\w+)\}/g, (_, k) => d[k] ?? "");
};

export const RealtimeProvider = ({ children }) => {
  const { user } = useAuth();
  const uid = user?.id;
  const { t } = useLang();
  const tRef = useRef(t);
  tRef.current = t;
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [connected, setConnected] = useState(false);
  const listeners = useRef(new Set());

  const load = useCallback(() => api.get("/notifications").then((r) => { setItems(r.data.items); setUnread(r.data.unread); }).catch(() => {}), []);

  useEffect(() => {
    if (!uid) return undefined;
    load();
    let ws, retry, closed = false;
    const connect = () => {
      ws = new WebSocket(`${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/ws`);
      ws.onopen = () => setConnected(true);
      ws.onmessage = (e) => {
        const msg = JSON.parse(e.data);
        if (msg.kind === "notification") {
          setItems((xs) => [msg.data, ...xs].slice(0, 50));
          setUnread((u) => u + 1);
          toast(fmt(tRef.current, msg.data));
        }
        listeners.current.forEach((fn) => fn(msg));
      };
      ws.onclose = async (e) => {
        setConnected(false);
        if (closed) return;
        if (e.code === 4401) await api.post("/auth/refresh").catch(() => {});
        retry = setTimeout(connect, 3000);
      };
    };
    connect();
    const ping = setInterval(() => ws?.readyState === 1 && ws.send("ping"), 25000);
    return () => { closed = true; clearTimeout(retry); clearInterval(ping); ws?.close(); };
  }, [uid, load]);

  const subscribe = useCallback((fn) => { listeners.current.add(fn); return () => listeners.current.delete(fn); }, []);
  const markAll = useCallback(async () => { await api.post("/notifications/read-all"); setUnread(0); setItems((xs) => xs.map((x) => ({ ...x, read: true }))); }, []);
  const markOne = useCallback(async (id) => {
    await api.post(`/notifications/${id}/read`);
    setItems((xs) => xs.map((x) => (x.id === id ? { ...x, read: true } : x)));
    setUnread((u) => Math.max(0, u - 1));
  }, []);

  const actions = useMemo(() => ({ subscribe, markAll, markOne }), [subscribe, markAll, markOne]);
  const value = useMemo(() => ({ items, unread, connected, ...actions }), [items, unread, connected, actions]);
  return <RTCtx.Provider value={value}>{children}</RTCtx.Provider>;
};

export const useRealtime = () => useContext(RTCtx);

export const useLiveEvent = (kinds, handler) => {
  const { subscribe } = useRealtime();
  const ref = useRef(handler);
  ref.current = handler;
  const key = kinds.join();
  useEffect(() => { const ks = key.split(","); return subscribe((m) => ks.includes(m.kind) && ref.current(m)); }, [subscribe, key]);
};
